/* eslint-disable import/prefer-default-export */
import { computed } from 'vue';
import { toValue } from '@vueuse/core';
import { Cookie } from '@vue-util/cookie';
import { useAutoReplenishment } from '@modules/auto-replenishment';
import { cookies } from 'config';
import { useFeatureEligibility, useProductFlags } from '@modules/featureControl';
import { useUpc, useUpcValidator, UPC_VALIDATION_MESSAGES } from '@modules/upc';
import { useSelectedProtectionPlan } from '@composables/useSelectedProtectionPlan';
import { useBrand } from '@composables/useBrand';
import { useBigTicketATBValidator, useFulfilmentData } from '@modules/availability';
import { useMarketPlace } from '@composables/useMarketPlace';
import { useQuantity } from '@modules/product';
import { useATBUTag } from './useATBUTag';
import { useBagContext } from './useBagContext';
/**
 * * Prepare and validate the payload for the ATB API request.
 *x
 * @description This composable provides a way to build the payload for the ATB API request and validate it.
 * It is used in the ATB composable (DI-like pattern).
 * It allows excluding the payload building and validation logic from the ATB composable.
 * It helps to keep the ATB composable clean and simple and let it focus on the ATB functionality.
 * As well it should help move ATB composable to the separate package in the future.
 * That way we can easily mock the payload and the payload validator in the ATB composable tests.
 * Also, it is easier to test the payload validator separately.
 *
 * @example
 * ```js
 * import { useATBPayload } from '@composables/useATBPayload';
 * import { useAddToBag } from '@modules/add-to-bag';
 * const { payload, payloadValidator } = useATBPayload();
 *
 * const { ... } = useAddToBag(payload, { payloadValidator });
 * ```
 * @param {Object} [options]
 * @param {import('@vueuse/shared').MaybeRefOrGetter<Partial<ATBPayloadItem>>} [options.additionalPayload]
 * @param {import('@vueuse/shared').MaybeRefOrGetter<ATBSourceUtagData|undefined>} [options.sourceUtagData]
 */
export const useATBPayload = ({ additionalPayload, sourceUtagData } = {}) => {
  const { quantity } = useQuantity();
  const { isMcom } = useBrand();
  const bagContext = useBagContext();

  const { selectedUpcId, selectedUpcNumber } = useUpc();
  const { bigTicketATBValidator } = useBigTicketATBValidator();
  const { upcValidator } = useUpcValidator();
  const { frequency, frequencySelected } = useAutoReplenishment();
  const { protectionPlan } = useSelectedProtectionPlan();
  const sourceUtagDataMain = useATBUTag();
  const { storeLocation, selectedRadioButton } = useFulfilmentData();
  const { isProtectionPlanEligible } = useFeatureEligibility();

  const { isMarketPlaceEligibleFlag } = useProductFlags();
  const { sellerId } = useMarketPlace();

  /**
   * @type {import('vue').ComputedRef<ATBPayload>}
   */
  const payload = computed(() => {
    const upcId = selectedUpcId.value?.toString();
    const upcNumber = selectedUpcNumber.value?.toString();
    const optedProtectionPlanId = protectionPlan.value;
    const registryId = 0; // TODO
    const source = 'PDPA2B';
    const loyaltyPoints = true;
    // prodSelectionInfo      // TODO
    let pickupPayload = {
      pickUpFromStore: 'false',
    };

    if (selectedRadioButton.value === 'Pickup') {
      pickupPayload = {
        ...pickupPayload,
        pickUpFromStore: 'true',
        deliveryOption: 'pickInStore',
        storeLocation: storeLocation.value?.toString(),
      };
    }

    return {
      data: {
        item: {
          /**
           * For some reason, upcId and upcNumber shoud be strings for Gift Cards pages.
           * It works fine with numbers for other pages.
           * I've checked if it's possible always to use strings for upcId and upcNumber.
           * And it works fine for all pages (28 March 2024).
           */
          upcId,
          upcNumber,
          registryId,
          source,
          loyaltyPoints,
          quantity: quantity.value,
          ...pickupPayload,
          ...additionalPayload ? toValue(additionalPayload) : {},
          ...optedProtectionPlanId ? { optedProtectionPlanId } : {},
          pdpAtbProtectionPlanEligible: isProtectionPlanEligible,
          isMarketPlaceEligibleFlag, // TODO: Does XAPI need this flag?
          sellerId: sellerId.value,
          autoreplenishment: frequencySelected.value ? {
            frequency: frequency.value,
            frequencyPeriod: 1,
          } : null,
        },
      },
      options: {
        ...frequencySelected.value ? { frequency: frequency.value, frequencyPeriod: 1 } : {},
        ...bagContext.isUpdateBagEnabled ? { sequenceNumber: bagContext.sequenceNumber } : {},
        sourceUtagData: {
          ...sourceUtagDataMain.value,
          ...sourceUtagData ? toValue(sourceUtagData) : {},
        },
        bagGuid: Cookie.get(cookies.bagGuid),
      },
      flags: {
        isMcom,
      },
    };
  });

  /**
   * @type {ATBPayloadValidator}
   */
  const payloadValidator = () => {
    const btValidationMessage = bigTicketATBValidator();
    if (btValidationMessage) return btValidationMessage;
    if (isMarketPlaceEligibleFlag && (!sellerId || Number(sellerId) === -1)) {
      return UPC_VALIDATION_MESSAGES.SIZE_COLOR_NOT_AVAILABLE;
    }
    return upcValidator();
  };

  return {
    payload,
    payloadValidator,
  };
};
